import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  FormErrorMessage,
  Grid,
  GridItem,
  VStack,
} from "@chakra-ui/react";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import { componentState } from "../../../../atoms/componentState";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { LISTAGEMVENDASRECORRENTES } from "../../../../constants/paths";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PatternFormat } from "react-number-format";
import { dateForAPI } from "../../../../constants/dataFormat";
import moment from "moment";
import { cnpj as cnpjValidator, cpf as cpfValidator } from "cpf-cnpj-validator";
import { toast } from "react-toastify";
import VendasRecorrentesApi from "../../../../services/vendasRecorrentesApi";
import PlanosRecorrenciaSelect from "../../../../defaultComponents/planosRecorrenciaSelect";
import { customColors } from "../../../../atoms/customColors";
import { useRecoilState } from "recoil";
import { JWTToken } from "../../listaDeVendas/SalesList";
import jwt_decode from "jwt-decode";
import axios from "axios";

interface IFormInputs {
  idPlano: string;
  dataProximaCobranca: string;
  dataExpiracao: string;
  valor: number;
  nome: string;
  sobrenome: string;
  cnpjPagador: string;
  emailPagador: string;
  telefonePagador: string;
  enderRua: string;
  enderCep: string;
  enderNumero: string;
  enderComplemento: string;
  enderBairro: string;
  enderMunicipio: string;
  enderUF: string;
  dataNasc: string;
  titular: string;
  numero: string;
  mes: string;
  ano: string;
  cvv: string;
}

const schema = yup
  .object({
    idPlano: yup.string().required("O plano é obrigatório"),
    dataProximaCobranca: yup
      .string()
      .required("A data da primeira cobrança é obrigatória")
      .test("future-date", "A data deve ser futura", (value) =>
        moment(value).isSameOrAfter(moment(), "day")
      ),
    dataExpiracao: yup.string().optional(),
    valor: yup
      .number()
      .required("O valor é obrigatório")
      .min(0, "O valor deve ser maior ou igual a zero"),
    nome: yup
      .string()
      .required("O nome é obrigatório")
      .min(2, "O nome deve ter no mínimo 2 caracteres")
      .max(100, "O nome não pode exceder 100 caracteres"),
    sobrenome: yup
      .string()
      .required("O sobrenome é obrigatório")
      .min(2, "O sobrenome deve ter no mínimo 2 caracteres"),
    cnpjPagador: yup
      .string()
      .required("CPF/CNPJ é obrigatório")
      .test(
        "cpf-cnpj-length",
        "CPF deve ter 11 dígitos ou CNPJ 14 dígitos",
        (value) => {
          if (!value) return false;
          const unmasked = unMask(value);
          return unmasked.length === 11 || unmasked.length === 14;
        }
      )
      .test("cpf-cnpj-valid", "CPF/CNPJ inválido", (value) => {
        if (!value) return false;
        const unmasked = unMask(value);
        return unmasked.length === 11
          ? cpfValidator.isValid(unmasked)
          : cnpjValidator.isValid(unmasked);
      }),
    emailPagador: yup
      .string()
      .required("O email é obrigatório")
      .email("Email inválido"),
    telefonePagador: yup
      .string()
      .required("O telefone é obrigatório")
      .matches(
        /^\d{10,11}$/,
        "Telefone inválido, deve conter 10 ou 11 dígitos"
      ),
    dataNasc: yup
      .string()
      .required("Data de nascimento é obrigatória")
      .test("valid-date", "Data de nascimento inválida", (value) =>
        value
          ? moment(value).isValid() && moment(value).isBefore(moment())
          : false
      ),
    enderCep: yup
      .string()
      .required("CEP é obrigatório")
      .matches(/^\d{8}$/, "CEP inválido, deve conter 8 dígitos"),
    enderRua: yup.string().required("Rua é obrigatória"),
    enderNumero: yup.string().required("Número é obrigatório"),
    enderBairro: yup.string().required("Bairro é obrigatório"),
    enderMunicipio: yup.string().required("Município é obrigatório"),
    enderUF: yup
      .string()
      .required("UF é obrigatória")
      .matches(/^[A-Z]{2}$/, "UF inválida"),
    enderComplemento: yup.string().optional(),
    titular: yup.string().required("O nome do titular é obrigatório"),
    numero: yup
      .string()
      .required("O número do cartão é obrigatório")
      .matches(/^\d{16}$/, "Número do cartão inválido, deve conter 16 dígitos"),
    mes: yup
      .string()
      .required("O mês de vencimento é obrigatório")
      .matches(/^(0[1-9]|1[0-2])$/, "Mês inválido (01-12)"),
    ano: yup
      .string()
      .required("O ano de vencimento é obrigatório")
      .matches(/^\d{4}$/, "Ano inválido (ex: 2025)")
      .test("future-year", "Ano deve ser atual ou futuro", (value) =>
        value ? parseInt(value) >= new Date().getFullYear() : false
      ),
    cvv: yup
      .string()
      .required("O CVV é obrigatório")
      .matches(/^\d{3,4}$/, "CVV inválido"),
  })
  .required();

const RequiredFormLabel = ({
  children,
  ...props
}: {
  children: React.ReactNode;
}) => (
  <FormLabel fontSize="sm" color="gray.600" {...props}>
    {children}{" "}
    <Text as="span" color="red.500">
      *
    </Text>
  </FormLabel>
);

const unMask = (value: any) => {
  return value.replace(/[^0-9]/g, "");
};

export default function AddVendaRecorrente() {
  const setHeaderLocation = useSetRecoilState(componentState);
  setHeaderLocation("Cadastrar Venda Recorrente");
  const navigate = useNavigate();
  const [colors] = useRecoilState(customColors);
  const [cpfCnpjValidation, setCpfCnpjValidation] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<IFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      idPlano: "",
      dataProximaCobranca: "",
      dataExpiracao: "",
      valor: 0,
      nome: "",
      sobrenome: "",
      cnpjPagador: "",
      emailPagador: "",
      telefonePagador: "",
      enderRua: "",
      enderCep: "",
      enderNumero: "",
      enderComplemento: "",
      enderBairro: "",
      enderMunicipio: "",
      enderUF: "",
      dataNasc: "",
      titular: "",
      numero: "",
      mes: "",
      ano: "",
      cvv: "",
    },
  });

  const token = window.localStorage.getItem("token");
  const jwtToken: JWTToken = jwt_decode(token || "");
  const idCliente = Number(jwtToken.idCliente);
  const formValues = watch();

  const [phoneMask, setPhoneMask] = useState("(##) #####-####");

  const validateCPForCNPJ = (value: string) => {
    const unmasked = unMask(value);
    if (unmasked.length === 11) {
      setCpfCnpjValidation(!cpfValidator.isValid(unmasked));
    } else if (unmasked.length === 14) {
      setCpfCnpjValidation(!cnpjValidator.isValid(unmasked));
    } else {
      setCpfCnpjValidation(true);
    }
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    const unmaskedPhone = formValues.telefonePagador
      ? formValues.telefonePagador.replace(/[^0-9]/g, "")
      : "";
    setPhoneMask(
      unmaskedPhone.length <= 10 ? "(##) ####-####" : "(##) #####-####"
    );
  }, [formValues.telefonePagador]);

  useEffect(() => {
    const cep = formValues.enderCep || "";
    if (cep.length === 8) {
      axios
        .get(`https://viacep.com.br/ws/${cep}/json/`)
        .then((response) => {
          if (!response.data.erro) {
            setValue("enderRua", response.data.logradouro || "");
            setValue("enderBairro", response.data.bairro || "");
            setValue("enderMunicipio", response.data.localidade || "");
            setValue("enderUF", response.data.uf || "");
          } else {
            toast.error("CEP não encontrado");
          }
        })
        .catch(() => toast.error("Erro ao consultar CEP"));
    }
  }, [formValues.enderCep, setValue]);

  const onSubmit = async (data: IFormInputs) => {
    const unmaskedCnpj = unMask(data.cnpjPagador);
    if (unmaskedCnpj.length === 11 && !cpfValidator.isValid(unmaskedCnpj)) {
      setCpfCnpjValidation(true);
      toast.error("CPF inválido");
      return;
    }
    if (unmaskedCnpj.length === 14 && !cnpjValidator.isValid(unmaskedCnpj)) {
      setCpfCnpjValidation(true);
      toast.error("CNPJ inválido");
      return;
    }

    const objectForApi = {
      idPlano: data.idPlano,
      dataProximaCobranca: moment(data.dataProximaCobranca).format(dateForAPI),
      dataExpiracao: data.dataExpiracao
        ? moment(data.dataExpiracao).format(dateForAPI)
        : undefined,
      valor: Number(data.valor) * 100,
      comprador: {
        nome: data.nome,
        sobrenome: data.sobrenome,
        email: data.emailPagador,
        cpf_cnpj: unmaskedCnpj,
        telefone: data.telefonePagador,
        endereco: {
          logradouro: data.enderRua,
          numero: data.enderNumero,
          bairro: data.enderBairro,
          cidade: data.enderMunicipio,
          estado: data.enderUF,
          cep: data.enderCep,
          complemento: data.enderComplemento,
        },
      },
      cartao: {
        titular: data.titular,
        numero: data.numero,
        mes: data.mes,
        ano: data.ano,
        cvv: data.cvv,
      },
    };

    try {
      await VendasRecorrentesApi.postVendaRecorrente(objectForApi, idCliente);
      toast.success("Venda Recorrente cadastrada com sucesso!");
      navigate(LISTAGEMVENDASRECORRENTES);
    } catch (error: any) {
      toast.error(
        error.response?.data?.join("; ") ||
          "Não foi possível cadastrar a venda!"
      );
      console.error(error);
    }
  };

  return (
    <Flex
      flexDir="column"
      minHeight="100vh"
      height="100vh"
      width="100%"
      bg="white"
      overflow="hidden"
      p={2}
    >
      {/* Header with Button Layout */}
      <Box
        width="99.8%"
        bgColor={colors.WHITE}
        height="fit-content"
        shadow="md"
        mt="0.2%"
        mb="50px"
        mx="auto"
        py={3}
        px={3}
      >
        <Flex justifyContent="flex-end" flexDirection="row" my="auto" px={8}>
          <Button
            leftIcon={<FaArrowLeft />}
            style={{ color: "white" }}
            onClick={() => navigate(LISTAGEMVENDASRECORRENTES)}
            _hover={{ bg: "#4B4B57" }}
            bgColor={colors.GRAY}
          >
            Voltar a Lista de Assinaturas
          </Button>
          <Button
            leftIcon={<FaSave />}
            mx={3}
            style={{ color: "white" }}
            isLoading={isSubmitting}
            onClick={handleSubmit(onSubmit)}
            _hover={{ bg: "#4B4B57" }}
            bgColor={colors.BLUEMONEDDBUTTONS}
          >
            Salvar Venda Recorrente
          </Button>
        </Flex>
      </Box>

      {/* Content with Scroll */}
      <Box
        w={{ base: "100%", md: "80%" }}
        maxW="1200px"
        bgColor={colors.WHITE}
        shadow="lg"
        mx="auto"
        p={6}
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.200"
        overflowY="auto"
        maxHeight="calc(85vh - 80px)"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack spacing={6} align="stretch">
            {/* Subscription Section */}
            <Box
              border="1px solid"
              borderColor="gray.100"
              p={4}
              borderRadius="md"
            >
              <Text fontSize="xl" fontWeight="bold" mb={4} color="gray.700">
                Informações da Assinatura
              </Text>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
              >
                <GridItem>
                  <FormControl isInvalid={!!errors.idPlano} mb={4}>
                    <RequiredFormLabel>Plano</RequiredFormLabel>
                    <PlanosRecorrenciaSelect
                      plano={formValues.idPlano}
                      setPlano={(value: string) => setValue("idPlano", value)}
                    />
                    <FormErrorMessage>
                      {errors.idPlano?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.dataProximaCobranca} mb={4}>
                    <RequiredFormLabel>
                      Data da Primeira Cobrança
                    </RequiredFormLabel>
                    <Input
                      type="date"
                      {...register("dataProximaCobranca")}
                      min={new Date().toISOString().split("T")[0]}
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.dataProximaCobranca?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.dataExpiracao} mb={4}>
                    <FormLabel fontSize="sm" color="gray.600">
                      Data de Expiração
                    </FormLabel>
                    <Input
                      type="date"
                      {...register("dataExpiracao")}
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.dataExpiracao?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.valor} mb={4}>
                    <RequiredFormLabel>Valor (R$)</RequiredFormLabel>
                    <Input
                      type="number"
                      step="0.01"
                      {...register("valor", { valueAsNumber: true })}
                      placeholder="0.00"
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>{errors.valor?.message}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/* Buyer Section */}
            <Box
              border="1px solid"
              borderColor="gray.100"
              p={4}
              borderRadius="md"
            >
              <Text fontSize="xl" fontWeight="bold" mb={4} color="gray.700">
                Informações do Comprador
              </Text>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
              >
                <GridItem>
                  <FormControl isInvalid={!!errors.nome} mb={4}>
                    <RequiredFormLabel>Primeiro Nome</RequiredFormLabel>
                    <Input
                      {...register("nome")}
                      placeholder="Nome"
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>{errors.nome?.message}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.sobrenome} mb={4}>
                    <RequiredFormLabel>Último Nome</RequiredFormLabel>
                    <Input
                      {...register("sobrenome")}
                      placeholder="Sobrenome"
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.sobrenome?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl
                    isInvalid={!!errors.cnpjPagador || cpfCnpjValidation}
                    mb={4}
                  >
                    <RequiredFormLabel>CPF/CNPJ</RequiredFormLabel>
                    <Input
                      type="text"
                      placeholder="Digite CPF ou CNPJ (somente números)"
                      maxLength={14}
                      {...register("cnpjPagador")}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/[^0-9]/g, "")
                          .slice(0, 14);
                        setValue("cnpjPagador", value);
                      }}
                      onBlur={(e) => validateCPForCNPJ(e.target.value)}
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    {cpfCnpjValidation && (
                      <Text color={colors.REDMONEDD} fontSize="sm">
                        CPF ou CNPJ inválido
                      </Text>
                    )}
                    <FormErrorMessage>
                      {errors.cnpjPagador?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.emailPagador} mb={4}>
                    <RequiredFormLabel>Email</RequiredFormLabel>
                    <Input
                      {...register("emailPagador")}
                      placeholder="Email"
                      type="email"
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.emailPagador?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.telefonePagador} mb={4}>
                    <RequiredFormLabel>Telefone</RequiredFormLabel>
                    <PatternFormat
                      format={phoneMask}
                      customInput={Input}
                      placeholder="Telefone"
                      allowEmptyFormatting
                      mask="_"
                      {...register("telefonePagador")}
                      onValueChange={(values) => {
                        setValue("telefonePagador", values.value);
                        setPhoneMask(
                          values.value.length <= 10
                            ? "(##) ####-####"
                            : "(##) #####-####"
                        );
                      }}
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.telefonePagador?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.dataNasc} mb={4}>
                    <RequiredFormLabel>Data de Nascimento</RequiredFormLabel>
                    <Input
                      type="date"
                      {...register("dataNasc")}
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.dataNasc?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/* Address Section */}
            <Box
              border="1px solid"
              borderColor="gray.100"
              p={4}
              borderRadius="md"
            >
              <Text fontSize="xl" fontWeight="bold" mb={4} color="gray.700">
                Endereço
              </Text>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
              >
                <GridItem>
                  <FormControl isInvalid={!!errors.enderCep} mb={4}>
                    <RequiredFormLabel>CEP</RequiredFormLabel>
                    <PatternFormat
                      format="#####-###"
                      customInput={Input}
                      placeholder="CEP"
                      allowEmptyFormatting
                      mask="_"
                      {...register("enderCep")}
                      onValueChange={(values) =>
                        setValue("enderCep", values.value)
                      }
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.enderCep?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.enderRua} mb={4}>
                    <RequiredFormLabel>Rua</RequiredFormLabel>
                    <Input
                      {...register("enderRua")}
                      placeholder="Rua"
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.enderRua?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.enderNumero} mb={4}>
                    <RequiredFormLabel>Número</RequiredFormLabel>
                    <Input
                      {...register("enderNumero")}
                      placeholder="Número"
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.enderNumero?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.enderBairro} mb={4}>
                    <RequiredFormLabel>Bairro</RequiredFormLabel>
                    <Input
                      {...register("enderBairro")}
                      placeholder="Bairro"
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.enderBairro?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.enderMunicipio} mb={4}>
                    <RequiredFormLabel>Município</RequiredFormLabel>
                    <Input
                      {...register("enderMunicipio")}
                      placeholder="Município"
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.enderMunicipio?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.enderUF} mb={4}>
                    <RequiredFormLabel>UF</RequiredFormLabel>
                    <Input
                      {...register("enderUF")}
                      placeholder="UF"
                      maxLength={2}
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.enderUF?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem colSpan={2}>
                  <FormControl mb={4}>
                    <FormLabel fontSize="sm" color="gray.600">
                      Complemento
                    </FormLabel>
                    <Input
                      {...register("enderComplemento")}
                      placeholder="Complemento"
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.enderComplemento?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/* Card Section */}
            <Box
              border="1px solid"
              borderColor="gray.100"
              p={4}
              borderRadius="md"
            >
              <Text fontSize="xl" fontWeight="bold" mb={4} color="gray.700">
                Informações do Cartão
              </Text>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
              >
                <GridItem>
                  <FormControl isInvalid={!!errors.titular} mb={4}>
                    <RequiredFormLabel>Nome do Titular</RequiredFormLabel>
                    <Input
                      {...register("titular")}
                      placeholder="Nome do Titular"
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.titular?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.numero} mb={4}>
                    <RequiredFormLabel>Número do Cartão</RequiredFormLabel>
                    <PatternFormat
                      format="#### #### #### ####"
                      customInput={Input}
                      placeholder="Número do Cartão"
                      allowEmptyFormatting
                      mask="_"
                      {...register("numero")}
                      onValueChange={(values) =>
                        setValue("numero", values.value)
                      }
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>
                      {errors.numero?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.mes} mb={4}>
                    <RequiredFormLabel>Mês de Vencimento</RequiredFormLabel>
                    <Input
                      {...register("mes")}
                      placeholder="MM"
                      maxLength={2}
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>{errors.mes?.message}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.ano} mb={4}>
                    <RequiredFormLabel>Ano de Vencimento</RequiredFormLabel>
                    <Input
                      {...register("ano")}
                      placeholder="AAAA"
                      maxLength={4}
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>{errors.ano?.message}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.cvv} mb={4}>
                    <RequiredFormLabel>CVV</RequiredFormLabel>
                    <Input
                      {...register("cvv")}
                      placeholder="CVV"
                      maxLength={4}
                      bg="gray.50"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage>{errors.cvv?.message}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>
          </VStack>
        </form>
      </Box>
    </Flex>
  );
}
